import { Card, Row, Col, Spinner, Alert } from "react-bootstrap";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  Legend,
} from "recharts";
import PageFade from "../../components/animation/PageFade";
import { StaggerFade } from "../../components/animation/StaggerFade";
import mock from "../../data/mockDashboard";
import { useGetAnalyticsQuery } from "../../services/api";

export default function Dashboard() {
  const {
    data: analytics,
    isLoading: analyticsLoading,
    error: analyticsError,
  } = useGetAnalyticsQuery();

  const productAnalytics = analytics?.data?.products || [];
  const topProducts = [...productAnalytics]
    .sort((a, b) => b.clicks - a.clicks)
    .slice(0, 8)
    .map((p) => ({
      id: p._id,
      name: p.name.length > 18 ? `${p.name.slice(0, 15)}…` : p.name,
      clicks: p.clicks,
      price: p.price,
    }));

  // Countries & Cities analytics (based on API shape)
  // Example:
  // countries: [{ totalVisits: 4, country: 'Egypt' }]
  // cities: [{ city: 'Port Said', country: 'Egypt', visitCount: 4 }]
  const rawCountries = (analytics as any)?.data?.countries || [];
  const rawCities = (analytics as any)?.data?.cities || [];

  const countriesData = rawCountries
    .map((item: any) => ({
      name: item.country || "Unknown",
      value: item.totalVisits ?? 0,
    }))
    .filter((d: any) => d.value > 0)
    .sort((a: any, b: any) => b.value - a.value);

  const citiesData = rawCities
    .map((item: any) => ({
      name: item.city || item.country || "Unknown",
      value: item.visitCount ?? item.totalVisits ?? 0,
    }))
    .filter((d: any) => d.value > 0)
    .sort((a: any, b: any) => b.value - a.value);

  return (
    <PageFade>
      {/* KPI Cards */}
     {/* <Row className="row-cols-1 row-cols-sm-2 row-cols-md-4 mb-4">
  {mock.kpis.map((kpi) => (
    <Col key={kpi.label} className="mb-3">
      <StaggerFade>
        <Card className="shadow-sm h-100 d-flex">
          <Card.Body className="d-flex flex-column gap-3">
            <div className="d-flex align-items-center justify-content-between">
              <Card.Title className="mb-0">{kpi.label}</Card.Title>
              <span
                className={`badge ${
                  kpi.trend > 0
                    ? "bg-success-subtle text-success"
                    : "bg-danger-subtle text-danger"
                }`}
              >
                {kpi.trend > 0 ? "▲" : "▼"} {Math.abs(kpi.trend)}%
              </span>
            </div>

            <div className="display-6 fw-semibold">{kpi.value}</div>

            <div className="text-muted small">vs last period</div>
          </Card.Body>
        </Card>
      </StaggerFade>
    </Col>
  ))}
</Row> */}


      {/* Sales Chart */}
      <Card className="shadow-sm p-3">
        <Card.Body style={{ height: 320 }}>
          <Card.Title>Sales (30d)</Card.Title>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={mock.sales}>
              <XAxis dataKey="day" />
              <YAxis />
              <Tooltip />
              <Line
                type="monotone"
                dataKey="value"
                stroke="#0d6efd"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </Card.Body>
      </Card>

      {/* Analytics: Top Products by Clicks */}
      <Card className="shadow-sm p-3 mt-4">
        <Card.Body style={{ height: 360 }}>
          <Card.Title>Top Products by Clicks</Card.Title>
          {analyticsLoading ? (
            <div className="d-flex justify-content-center align-items-center h-100">
              <Spinner animation="border" role="status">
                <span className="visually-hidden">Loading analytics...</span>
              </Spinner>
            </div>
          ) : analyticsError ? (
            <Alert variant="warning" className="mt-3">
              Unable to load analytics. Please try again later.
            </Alert>
          ) : topProducts.length === 0 ? (
            <div className="text-muted small mt-3">
              No analytics data available yet. Analytics will appear here as customers interact with products.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topProducts}
                margin={{ top: 16, right: 24, bottom: 8, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis />
                <Tooltip
                  formatter={(value: any, name: any) =>
                    name === "clicks" ? [`${value}`, "Clicks"] : [`EGP ${value}`, "Price"]
                  }
                />
                <Legend />
                <Bar dataKey="clicks" name="Clicks" fill="#0d6efd" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card.Body>
      </Card>

      {/* Analytics: Geography (Countries & Cities) */}
      <Row className="mt-4">
        <Col md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <Card.Body style={{ height: 320 }}>
              <Card.Title>Top Countries</Card.Title>
              {analyticsLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading analytics...</span>
                  </Spinner>
                </div>
              ) : analyticsError ? (
                <Alert variant="warning" className="mt-3">
                  Unable to load country analytics.
                </Alert>
              ) : countriesData.length === 0 ? (
                <div className="text-muted small mt-3">
                  No country analytics data available yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={countriesData}
                    margin={{ top: 16, right: 24, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="value"
                      name="Sessions / Clicks"
                      fill="#198754"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col md={6} className="mb-3">
          <Card className="shadow-sm h-100">
            <Card.Body style={{ height: 320 }}>
              <Card.Title>Top Cities</Card.Title>
              {analyticsLoading ? (
                <div className="d-flex justify-content-center align-items-center h-100">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Loading analytics...</span>
                  </Spinner>
                </div>
              ) : analyticsError ? (
                <Alert variant="warning" className="mt-3">
                  Unable to load city analytics.
                </Alert>
              ) : citiesData.length === 0 ? (
                <div className="text-muted small mt-3">
                  No city analytics data available yet.
                </div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={citiesData}
                    margin={{ top: 16, right: 24, bottom: 8, left: 0 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis />
                    <Tooltip />
                    <Bar
                      dataKey="value"
                      name="Sessions / Clicks"
                      fill="#fd7e14"
                      radius={[4, 4, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </PageFade>
  );
}
